// src/view/itemView.ts — Yerdeki / uçan eşya propları (ANIMATION §7, DESIGN §9): yerde yatık yönelim + Y salınımı ±0.05 u 1 Hz + yaw
// 0.5 rad/s; uçarken yerel X ekseninde 3 tur/s dönüş; son 3 s yanıp sönme. 'held' eşyayı fighterView gösterir (burada gizli).
// Doğuş efekti vfx.ts'de (Ev.ItemSpawn: daralan altın halka + kıvılcım; eski mavi ışık sütunu kaldırıldı — kullanıcı geri bildirimi);
// buradaki eşi: eşya DÜŞERKEN hızlı yaw + salınımsız, YERE DEĞİNCE kısa bir ölçek "pop"u. Item.prevPos yok → tick değişiminde
// kendi anlık görüntüsüyle interpolasyon.
import * as THREE from 'three';
import type { Item, PropId, SimWorld, Vec3 } from '../core/types.ts';
import { MAX_ITEMS, NO_ID } from '../config/constants.ts';
import { T } from '../config/tuning.ts';
import { lerp } from '../core/math.ts';

/** Yanıp sönme frekansı (DESIGN §9 "son 3 s yanıp söner"; frekans belgede yok → 6 Hz, needsWP0). Yerde yatan propun kalınlık payı. */
const BLINK_HZ = 6;
const GROUND_LIFT = 0.08;
/** İniş "pop"u: eşya yere değince 0.16 s'de sönen ölçek fazlası (silindirsiz doğuş efektinin son vuruşu; belgede sayı yok → yerel). */
const POP_SEC = 0.16;
const POP_SCALE = 0.35;
/** Düşerken yaw hızı çarpanı: havada dönerek gelsin, yere değince yavaş sürüklenmeye insin. */
const AIR_YAW_MULT = 7;

interface Slot {
  itemId: number; propId: PropId | null; root: THREE.Object3D | null;
  prev: Vec3; cur: Vec3; tick: number;
  spinAngle: number; yawDrift: number; visible: boolean;
  /** Kalan pop süresi (s) ve bir önceki karede havada mıydı (iniş kenarını yakalamak için). */
  pop: number; wasAir: boolean;
}
const newSlot = (): Slot => ({ itemId: NO_ID, propId: null, root: null, prev: { x: 0, y: 0, z: 0 }, cur: { x: 0, y: 0, z: 0 }, tick: -1, spinAngle: 0, yawDrift: 0, visible: false, pop: 0, wasAir: false });

/** Yerde yatık yönelim: önce yaw (Y), sonra −90° pitch → prop yerel +Y (kılıç ucu) dünya XZ'de yatar. */
export function groundOrientation(out: THREE.Euler, yaw: number): THREE.Euler { return out.set(-Math.PI / 2, yaw, 0, 'YXZ'); }
/** Uçarken: yaw uçuş yönü, yerel X ekseninde spin (ANIMATION §7). */
export function flyingOrientation(out: THREE.Euler, yaw: number, spin: number): THREE.Euler { return out.set(spin, yaw, 0, 'YXZ'); }

export class ItemViews {
  private readonly slots: Slot[] = [];
  private time = 0;
  constructor(readonly scene: THREE.Object3D, readonly createProp: (id: PropId) => THREE.Object3D) {
    for (let i = 0; i < MAX_ITEMS; i++) this.slots.push(newSlot());
  }

  private slotFor(item: Item, idx: number): Slot {
    const s = this.slots[idx] ?? (this.slots[idx] = newSlot());
    if (s.itemId !== item.id || s.propId !== item.propId) {
      s.root?.removeFromParent();
      s.root = null; s.propId = null; s.itemId = item.id;
      if (item.state !== 'dead') {
        s.root = this.createProp(item.propId);
        s.root.name = 'item_' + item.id;
        s.propId = item.propId;
        this.scene.add(s.root);
      }
      s.prev.x = s.cur.x = item.pos.x; s.prev.y = s.cur.y = item.pos.y; s.prev.z = s.cur.z = item.pos.z;
      s.tick = -1; s.spinAngle = 0; s.yawDrift = 0; s.pop = 0; s.wasAir = false;
      s.root?.scale.set(1, 1, 1);
    }
    return s;
  }

  update(world: SimWorld, alpha: number, dt: number): void {
    this.time += dt;
    const items = world.items;
    for (let i = 0; i < this.slots.length; i++) {
      const item = items[i];
      const s = this.slots[i]!;
      if (!item) { if (s.root) { s.root.removeFromParent(); s.root = null; s.itemId = NO_ID; s.propId = null; } continue; }
      const slot = this.slotFor(item, i);
      const root = slot.root;
      if (!root) continue;
      if (item.state === 'dead' || item.state === 'held') { root.visible = false; continue; }
      // tick anlık görüntüsü → interpolasyon
      if (slot.tick !== world.tick) {
        slot.prev.x = slot.cur.x; slot.prev.y = slot.cur.y; slot.prev.z = slot.cur.z;
        slot.cur.x = item.pos.x; slot.cur.y = item.pos.y; slot.cur.z = item.pos.z;
        slot.tick = world.tick;
      }
      const x = lerp(slot.prev.x, slot.cur.x, alpha), y = lerp(slot.prev.y, slot.cur.y, alpha), z = lerp(slot.prev.z, slot.cur.z, alpha);
      if (item.state === 'flying') {
        slot.spinAngle += T.ITEMS.throwSpin * Math.PI * 2 * dt;   // sim Item.spin sözleşmesi (açı mı hız mı) belirsiz → view-yerel açı
        root.position.set(x, y, z);
        flyingOrientation(root.rotation, item.yaw, slot.spinAngle);
        root.scale.set(1, 1, 1); slot.pop = 0; slot.wasAir = false;
        root.visible = true;
      } else {
        // sim sözleşmesi (items.ts): 'ground' durumunda vel.y ≠ 0 = HAVADA (doğuşta 3 u'dan düşüyor ya da düşürülmüş).
        // Düşerken salınım yok (yerdeki eşyanın nefesi havada saçma durur) ve yaw hızlı; iniş kenarında pop tetiklenir.
        const air = item.vel.y !== 0;
        if (slot.wasAir && !air) slot.pop = POP_SEC;
        slot.wasAir = air;
        slot.yawDrift += T.ITEMS.groundYawRate * (air ? AIR_YAW_MULT : 1) * dt;
        const bob = air ? 0 : T.ITEMS.groundBobAmp * Math.sin(this.time * T.ITEMS.groundBobHz * Math.PI * 2);
        root.position.set(x, y + GROUND_LIFT + bob, z);
        groundOrientation(root.rotation, item.yaw + slot.yawDrift);
        if (slot.pop > 0) {
          slot.pop = Math.max(0, slot.pop - dt);
          const s = 1 + POP_SCALE * (slot.pop / POP_SEC);
          root.scale.set(s, s, s);
        } else if (root.scale.x !== 1) root.scale.set(1, 1, 1);
        const left = item.despawnAt - world.tick;
        root.visible = left > T.ITEMS.blinkBefore || Math.sin(this.time * BLINK_HZ * Math.PI * 2) > 0;
      }
    }
  }

  /** Sahnedeki (görünür) prop kökü; VFX/debug için. */
  rootOf(itemId: number): THREE.Object3D | null {
    for (const s of this.slots) if (s.itemId === itemId) return s.root;
    return null;
  }

  dispose(): void {
    for (const s of this.slots) { s.root?.removeFromParent(); s.root = null; s.itemId = NO_ID; s.propId = null; }
  }
}
